/**
 * A execução aberta — o que ela sabe, o que a conversa sabe, e sob o quê.
 *
 * **POR QUE ESTA TELA EXISTE.** O agregado diz QUANTO se perde; esta diz POR
 * QUÊ, e é para ela que cada linha das três seções do funil aponta. Quando o
 * escritório vê "33 conversas param entre pedir e localizar", a pergunta
 * seguinte é "me mostra uma dessas" — e até aqui não havia para onde ir.
 *
 * **TRÊS COISAS QUE ELA NÃO FAZ**, e cada uma tem razão escrita:
 *
 * 1. Não reexibe a conversa inteira. O histórico completo é link para o
 *    Chatwoot — reexibi-lo abriria caminho de leitura sobre `conversations` e
 *    `messages`, que a regra de ouro 5 manda não tocar.
 * 2. Não mostra seção de conversa nem de funil quando a execução não veio do
 *    atendimento. Elas **não aparecem**, em vez de aparecerem vazias.
 * 3. Não dá erro quando o texto expirou. Diz que expirou, e diz o que
 *    sobreviveu — dar 404 faria a retenção parecer defeito.
 */
import { Fragment, useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { execucoesService } from '@/services/painel'
import type { ExecucaoAberta, TurnoDaConversa } from '@/schemas/painel'
import { dolarMiudo, inteiro } from '@/lib/formato'
import { cn } from '@/lib/utils'

function duracao(ms: number | null): string {
  if (ms === null) return '—'
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`
}

/** Um número com o que ele é, em cima. */
function Cartao({
  rotulo,
  valor,
  nota,
}: {
  rotulo: string
  valor: string
  /** O outro número. Ver a nota sobre as duas unidades, mais abaixo. */
  nota?: string
}) {
  return (
    <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">
        {rotulo}
      </div>
      <div className="mt-1 text-[15px] tabular-nums text-gray-200">{valor}</div>
      {nota && (
        <div className="mt-0.5 text-[11px] tabular-nums text-gray-500">
          {nota}
        </div>
      )}
    </div>
  )
}

export function ExecucaoAbertaPage() {
  const { executionId } = useParams<{ executionId: string }>()
  const [execucao, setExecucao] = useState<ExecucaoAberta | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  const buscar = useCallback(async () => {
    if (!executionId) {
      // SEM ID NÃO SE FICA CARREGANDO PARA SEMPRE. Antes, o retorno antes do
      // `finally` deixava "Abrindo…" na tela sem nada acontecer.
      setErro('Endereço de execução inválido.')
      setCarregando(false)
      return
    }
    try {
      setExecucao(await execucoesService.abrir(executionId))
      setErro(null)
    } catch {
      setErro('Não foi possível abrir esta execução agora.')
    } finally {
      setCarregando(false)
    }
  }, [executionId])

  useEffect(() => {
    void buscar()
  }, [buscar])

  if (carregando) {
    return <p className="py-16 text-center text-gray-500">Abrindo…</p>
  }
  if (erro || !execucao) {
    // `!execucao` sem erro renderizava um parágrafo vermelho VAZIO — erro mudo
    // é o que a regra de ouro 4 proíbe.
    return (
      <p className="py-16 text-center text-red-400">
        {erro ?? 'Esta execução não foi encontrada.'}
      </p>
    )
  }

  const sob = execucao.sob_o_que_rodou
  const funil = execucao.caminho_no_funil
  // A CONDIÇÃO DE FR-020 É A SESSÃO. Prender "A conversa" ao funil escondia o
  // texto de execução do atendimento cujo objetivo ainda não abriu — o
  // backend mandava a mensagem e a tela não a desenhava.
  const doAtendimento = execucao.channel_session_id !== null

  // OS DOIS NÚMEROS, COMO NA LISTAGEM.
  //
  // O DEFEITO, RELATADO ASSIM: "por que está aparecendo 0 ferramentas e
  // somente 5k de tokens?". Não estava errado — aquele turno era um "Oi" com
  // resposta de saudação, 5.479 tokens e nenhuma ferramenta. O que engana é
  // a página mostrar a CONVERSA inteira embaixo e o número de UM TURNO em
  // cima, sem dizer qual é qual. Quem vem da lista de conversas lê o número
  // como sendo da conversa.
  //
  // Então vêm os dois, com o rótulo dizendo qual: a conversa em cima, o
  // turno na linha de baixo. Um número sem o outro faz quem lê concluir que
  // o relatório está errado.
  const conversa = execucao.turnos.length > 1 ? execucao.turnos : null
  const somaDaConversa = conversa && {
    tokens: conversa.reduce((t, x) => t + x.total_tokens, 0),
    custo: conversa.reduce((t, x) => t + (x.cost ?? 0), 0),
    ferramentas: conversa.reduce((t, x) => t + x.tool_calls.length, 0),
    // SOMA DO PROCESSAMENTO, e não o relógio de parede: o intervalo da
    // conversa está no cabeçalho e inclui o tempo em que ninguém trabalhou.
    // O que custa é este.
    tempo: conversa.reduce((t, x) => t + (x.execution_time_ms ?? 0), 0),
  }
  // ⚠️ A SOMA ENCOLHE DEPOIS DO EXPURGO, e isso precisa ser dito. O conteúdo
  // das ferramentas expira com as mensagens (ADR-0071), então um turno
  // expirado contribui com zero ferramentas para o total. Tokens e custo
  // sobrevivem; a contagem de ferramentas, não.
  const algumExpirado = execucao.turnos.some((t) => t.texto_expirado)
  // O TURNO ABERTO SAI DA MESMA LISTA QUE O TOTAL, e não dos campos soltos da
  // execução. São a mesma linha no banco, mas lê-los de fontes diferentes
  // deixa a porta aberta para a nota do cartão discordar do que a lista
  // mostra logo abaixo — a tela se contradizendo consigo na mesma rolagem.
  const aberto = execucao.turnos.find((t) => t.e_o_aberto)
  const doTurno = {
    tokens: aberto?.total_tokens ?? execucao.total_tokens,
    custo: aberto ? aberto.cost : execucao.cost,
    ferramentas: (aberto ?? execucao).tool_calls.length,
    tempo: aberto?.execution_time_ms ?? execucao.execution_time_ms,
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/executions"
            className="mb-2 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300"
          >
            <ArrowLeft className="h-3 w-3" /> Execuções
          </Link>
          {/* O CABEÇALHO IDENTIFICA O QUE A PÁGINA É.
              Ele nomeava a EXECUÇÃO — id do turno, horário do turno, duração
              do turno — numa página que mostra a conversa inteira. Quem
              clicou numa conversa via o cartão de um turno com a conversa
              embaixo, e concluía que os números estavam errados.

              Com conversa, o que identifica é a conversa: o id dela, o
              intervalo do primeiro ao último turno, e quantos foram. O turno
              aberto continua marcado, lá na lista, onde ele é uma linha. */}
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-100">
              {execucao.agent_name}
            </h1>
            <span className="font-mono text-xs text-gray-500">
              {conversa && execucao.channel_session_id
                ? `conversa ${execucao.channel_session_id.slice(0, 8)}`
                : execucao.id.slice(0, 8)}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {conversa ? (
              <>
                {`${execucao.turnos.length} turnos · `}
                {new Date(conversa[0].created_at).toLocaleString('pt-BR')}
                {` até ${new Date(
                  conversa[conversa.length - 1].created_at
                ).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}`}
              </>
            ) : (
              <>
                {new Date(execucao.created_at).toLocaleString('pt-BR')}
                {execucao.execution_time_ms !== null &&
                  // "CONCLUÍDA" SÓ QUANDO CONCLUIU. Execução que falhou também
                  // tem duração, e chamá-la de concluída esconde a falha no
                  // cabeçalho.
                  ` · ${execucao.status === 'success' ? 'concluída' : execucao.status} em ${duracao(execucao.execution_time_ms)}`}
              </>
            )}
          </p>
        </div>
        {execucao.link_no_chatwoot && (
          <a
            href={execucao.link_no_chatwoot}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-indigo-500/40 px-3 py-2 text-sm text-indigo-300 hover:bg-indigo-500/10"
          >
            abrir no Chatwoot <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      {/* EM ANDAMENTO É DITO, e não vira zero em tokens: a tela não sugere
          falha onde há trabalho em curso. */}
      {execucao.em_andamento ? (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-4 text-sm text-sky-200">
          Esta execução ainda está em andamento. Os números abaixo ficam
          incompletos até ela terminar — não são zero, são ainda-não.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Cartao
              rotulo={somaDaConversa ? 'Tempo de processamento' : 'Modelo'}
              valor={
                somaDaConversa
                  ? duracao(somaDaConversa.tempo)
                  : execucao.model || '—'
              }
              nota={
                somaDaConversa
                  ? `neste turno: ${duracao(doTurno.tempo)}`
                  : undefined
              }
            />
            <Cartao
              rotulo={somaDaConversa ? 'Tokens da conversa' : 'Tokens'}
              valor={inteiro(
                somaDaConversa ? somaDaConversa.tokens : execucao.total_tokens
              )}
              nota={
                somaDaConversa
                  ? `neste turno: ${inteiro(doTurno.tokens)}`
                  : undefined
              }
            />
            <Cartao
              rotulo={somaDaConversa ? 'Custo da conversa' : 'Custo'}
              valor={
                somaDaConversa
                  ? dolarMiudo(somaDaConversa.custo)
                  : execucao.cost === null
                    ? '—'
                    : dolarMiudo(execucao.cost)
              }
              nota={
                somaDaConversa
                  ? `neste turno: ${
                      doTurno.custo === null ? '—' : dolarMiudo(doTurno.custo)
                    }`
                  : undefined
              }
            />
            <Cartao
              rotulo={somaDaConversa ? 'Ferramentas na conversa' : 'Ferramentas'}
              valor={String(
                somaDaConversa
                  ? somaDaConversa.ferramentas
                  : execucao.tool_calls.length
              )}
              nota={
                somaDaConversa
                  ? `neste turno: ${doTurno.ferramentas}`
                  : undefined
              }
            />
          </div>
          {somaDaConversa && algumExpirado && (
            <p className="text-xs text-amber-500/70">
              Algum turno desta conversa já expirou: tokens e custo sobrevivem
              ao expurgo, a contagem de ferramentas não. O total acima é o que
              ainda existe.
            </p>
          )}
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* A IDENTIFICAÇÃO — e por que ela NÃO é uma etapa do funil.            */}
      {/*                                                                     */}
      {/* Perguntado assim: "e a etapa de identificação do cliente, por que   */}
      {/* não aparece na conversa?". Porque o funil desenha exatamente as     */}
      {/* etapas que o escritório declarou, e identificação não é uma delas — */}
      {/* um escritório pode nem exigir. Fazê-la virar etapa seria o produto  */}
      {/* decidir pelo documento (regra de ouro 1).                           */}
      {/*                                                                     */}
      {/* Mas o fato existe, e estava invisível. Ele vem de `session_events`, */}
      {/* ao lado do funil e não dentro dele.                                 */}
      {/*                                                                     */}
      {/* SEM O IDENTIFICADOR DO SUJEITO: aqueles eventos guardam doze meses  */}
      {/* e o conteúdo expira no prazo do escritório (ADR-0071). Ficam o      */}
      {/* nível, o motivo e a origem, que descrevem o mecanismo.              */}
      {/* ------------------------------------------------------------------ */}
      {execucao.identificacao && (
        <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
          <p className="mb-2 text-sm font-medium text-gray-300">
            A identificação
          </p>
          <div className="flex flex-wrap items-baseline gap-2 text-sm">
            <span className="rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-sky-300">
              {execucao.identificacao.nivel}
            </span>
            {/* QUEM. É a pergunta que originou esta seção, e ela vem do
                cadastro do escritório — não do payload do evento. O
                identificador sai mascarado porque é assim que a tabela o
                guarda; a coluna com o valor cheio é um hash. */}
            {execucao.identificacao.cliente && (
              <span className="font-medium text-gray-100">
                {execucao.identificacao.cliente}
              </span>
            )}
            {execucao.identificacao.identificador_mascarado && (
              <span className="font-mono text-xs text-gray-500">
                {execucao.identificacao.identificador_mascarado}
              </span>
            )}
            <span className="text-gray-600">·</span>
            {execucao.identificacao.motivo && (
              <span className="text-gray-300">
                {execucao.identificacao.motivo}
              </span>
            )}
            {execucao.identificacao.origem && (
              <span className="text-xs text-gray-500">
                (origem: {execucao.identificacao.origem})
              </span>
            )}
            <span className="flex-grow" />
            <span className="text-xs text-gray-500">
              {new Date(execucao.identificacao.quando).toLocaleString('pt-BR')}
            </span>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Não é etapa do funil: o funil desenha as etapas que o escritório
            declarou, e a identificação é um fato da conversa. Quem é o cliente
            aparece no resultado das ferramentas, que expira com as mensagens.
          </p>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* O CAMINHO NO FUNIL — só quando a execução veio do atendimento.      */}
      {/* ------------------------------------------------------------------ */}
      {funil && (
        <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
          <p className="mb-3 text-sm font-medium text-gray-300">
            O caminho no funil
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {funil.etapas_percorridas.map((etapa) => (
              <span
                key={etapa}
                className="rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-sm text-emerald-400"
              >
                {etapa}
              </span>
            ))}
            {funil.etapas_percorridas.length === 0 && (
              <span className="text-sm text-gray-500">
                nenhuma etapa alcançada
              </span>
            )}
          </div>
          <p className="mt-3 text-xs leading-relaxed text-gray-500">
            Objetivo{' '}
            <strong className="font-semibold text-gray-400">
              {funil.objetivo}
            </strong>{' '}
            · {funil.status}
            {funil.convertido_em &&
              ` às ${new Date(funil.convertido_em).toLocaleTimeString('pt-BR')}`}
            {funil.motivo && (
              <>
                {' · '}
                {funil.motivo}{' '}
                {/* A FONTE SEMPRE VISÍVEL: observado não se confunde com
                    inferido, e sem a fonte palpite vira medição. */}
                <span className="text-gray-600">
                  ({funil.fonte_do_motivo ?? 'sem fonte'})
                </span>
              </>
            )}
            {funil.houve_transbordo ? ' · com transbordo' : ' · sem transbordo'}
          </p>
          <p className="mt-1 text-xs text-gray-600">
            Os nomes das etapas são os do documento <strong>vigente</strong>. Se
            o escritório renomeou uma etapa depois deste turno, é o nome novo
            que aparece aqui — o número da versão acima é o do turno.
          </p>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* SOB O QUE ESTE TURNO RODOU                                          */}
      {/*                                                                     */}
      {/* Sem isto, toda execução passada alega — por omissão — ter rodado    */}
      {/* sob o prompt, o documento e o código de AGORA, e a pergunta "o que  */}
      {/* mudou entre a semana passada e esta?" não tem resposta gravada.     */}
      {/* ------------------------------------------------------------------ */}
      <div className="rounded-xl border border-indigo-500/25 bg-[#12121a] p-4">
        <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-sm font-medium text-gray-300">
            Sob o que este turno rodou
          </p>
          <p className="text-xs text-gray-500">
            carimbado na execução, não lido do estado atual
          </p>
        </div>
        <dl className="divide-y divide-gray-800/50 text-sm">
          <Linha
            rotulo="Documento do escritório"
            valor={
              sob.blueprint_version === null ? '—' : `v${sob.blueprint_version}`
            }
            nota="a versão do TURNO, não a da abertura da conversa"
          />
          <Linha
            rotulo="Instrução de sistema"
            valor={
              sob.system_prompt_sha256
                ? `sha256 ${sob.system_prompt_sha256.slice(0, 8)}…`
                : '—'
            }
            nota="só a marca: o texto tem vários KB e se repetiria"
          />
          <Linha
            rotulo="Ferramentas oferecidas"
            valor={
              sob.tools_offered.length === 0
                ? '—'
                : `${sob.tools_offered.length} — ${sob.tools_offered.join(', ')}`
            }
            nota="explica a ferramenta que NÃO foi chamada"
          />
          <Linha
            rotulo="Parâmetros do modelo"
            valor={
              Object.keys(sob.llm_params).length === 0
                ? '—'
                : Object.entries(sob.llm_params)
                    .map(([k, v]) => `${k} ${String(v)}`)
                    .join(' · ')
            }
            nota="resolvidos no turno, não lidos da configuração de hoje"
          />
          <Linha
            rotulo="Revisão do código"
            valor={sob.code_revision ?? '—'}
            nota="vem do ambiente, não de constante no arquivo"
          />
          <Linha
            rotulo="Modelo pedido"
            valor={sob.model_requested ?? '—'}
            nota={
              sob.model_resolved
                ? `o provedor resolveu para ${sob.model_resolved}`
                : 'o provedor não devolve a versão resolvida nesta integração'
            }
          />
        </dl>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* A CONVERSA — e o que acontece quando ela expira                     */}
      {/* ------------------------------------------------------------------ */}
      {doAtendimento && (
        <div className="rounded-xl border border-gray-800/50 bg-[#12121a] p-4">
          {/* A CONVERSA INTEIRA, e não o turno que a URL nomeia.
              A listagem tem uma linha por conversa desde 20/09, e abrir essa
              linha mostrava UM turno — o mais recente. Numa conversa de
              quatro, o pedido do cliente, a busca que localizou o documento e
              a entrega ficavam invisíveis: exatamente o que se vai ali
              procurar.

              `turnos` vem vazio de backend anterior a esta entrega — os dois
              sobem separados —, e aí a seção cai no turno único, que é o
              comportamento de antes e não uma conversa em branco. */}
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <p className="text-sm font-medium text-gray-300">A conversa</p>
            {execucao.turnos.length > 1 && (
              <p className="text-xs text-gray-500">
                {execucao.turnos.length === execucao.total_de_turnos
                  ? `${execucao.total_de_turnos} turnos`
                  : /* CORTE DECLARADO: em silêncio, quem lê conclui que a
                       conversa terminou onde a lista termina. */
                    `${execucao.turnos.length} dos ${execucao.total_de_turnos} turnos`}
              </p>
            )}
          </div>
          {execucao.turnos.length > 0 ? (
            <div className="space-y-4">
              {execucao.turnos.map((turno) => (
                <Turno
                  key={turno.id}
                  turno={turno}
                  destacado={turno.e_o_aberto && execucao.turnos.length > 1}
                />
              ))}
            </div>
          ) : execucao.texto_expirado ? (
            <div className="rounded-lg border border-dashed border-amber-700/50 p-5 text-center">
              <p className="text-gray-300">Conteúdo expirado</p>
              <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
                As mensagens expiram no prazo que o escritório declara. Tudo
                acima sobrevive — custo, caminho no funil, prompt, ferramentas e
                revisão do código — porque nada disso identifica pessoa. É o que
                permite auditar este turno sem guardar o que o cliente escreveu.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <Fala quem="cliente" texto={execucao.mensagem_do_cliente} />
              {execucao.tool_calls.length > 0 && (
                <div className="flex gap-3">
                  <div className="w-20 flex-shrink-0 pt-1 text-xs text-gray-500">
                    ferramenta
                  </div>
                  <div className="flex flex-grow flex-col gap-2">
                    {execucao.tool_calls.map((chamada, i) => (
                      <ChamadaDeFerramenta key={i} chamada={chamada} />
                    ))}
                  </div>
                </div>
              )}
              <Fala
                quem="assistente"
                texto={execucao.resposta_do_assistente}
                verde
              />
            </div>
          )}
        </div>
      )}

      {execucao.error_message && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
          {execucao.error_message}
        </div>
      )}
    </div>
  )
}

function Linha({
  rotulo,
  valor,
  nota,
}: {
  rotulo: string
  valor: string
  nota: string
}) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 py-2.5">
      <dt className="w-56 flex-shrink-0 text-gray-500">{rotulo}</dt>
      <dd className="flex-grow break-all font-mono text-xs text-gray-200">
        {valor}
      </dd>
      <dd className="w-full text-xs text-gray-600 md:w-72 md:text-right">
        {nota}
      </dd>
    </div>
  )
}

/**
 * Um turno dentro da conversa.
 *
 * O turno que a URL abriu fica destacado — sem a marca, quem clicou numa
 * linha perde de vista de onde veio assim que a conversa tem mais de um. O
 * destaque só aparece quando há mais de um: marcar o único seria ruído.
 *
 * O turno expirado PERMANECE na lista, dizendo que expirou. Sumir faria a
 * conversa parecer mais curta do que foi, que é o mesmo defeito que o expurgo
 * foi desenhado para não cometer.
 */
function Turno({
  turno,
  destacado,
}: {
  turno: TurnoDaConversa
  destacado: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        destacado ? 'border-indigo-500/40 bg-indigo-500/5' : 'border-gray-800/50'
      )}
    >
      <div className="mb-2 flex items-baseline gap-2 text-xs text-gray-500">
        <span>
          {new Date(turno.created_at).toLocaleTimeString('pt-BR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        {destacado && (
          <span className="rounded bg-indigo-500/15 px-1.5 py-0.5 text-indigo-300">
            este turno
          </span>
        )}
        <span className="flex-grow" />
        {/* OS TRÊS NÚMEROS DO TURNO, e o de tokens é o que explica os outros
            dois: um turno de saudação custa pouco porque manda pouco, e um
            que buscou no acervo carrega o resultado da busca no contexto.
            Sem ele, o custo varia entre turnos sem nada na tela que diga por
            quê. */}
        {turno.total_tokens > 0 && (
          <span className="tabular-nums">
            {inteiro(turno.total_tokens)} tokens
          </span>
        )}
        {turno.execution_time_ms !== null && (
          <span className="tabular-nums">
            {duracao(turno.execution_time_ms)}
          </span>
        )}
        {turno.cost !== null && (
          <span className="tabular-nums">{dolarMiudo(turno.cost)}</span>
        )}
      </div>
      {turno.texto_expirado ? (
        <p className="rounded-lg border border-dashed border-amber-700/40 px-3 py-2 text-sm text-amber-500/80">
          Conteúdo expirado — o turno permanece contando no funil.
        </p>
      ) : (
        <div className="space-y-2">
          <Fala quem="cliente" texto={turno.mensagem_do_cliente} />
          {turno.tool_calls.length > 0 && (
            <div className="flex gap-3">
              <div className="w-20 flex-shrink-0 pt-1 text-xs text-gray-500">
                ferramenta
              </div>
              <div className="flex flex-grow flex-col gap-2">
                {turno.tool_calls.map((chamada, i) => (
                  <ChamadaDeFerramenta key={i} chamada={chamada} />
                ))}
              </div>
            </div>
          )}
          <Fala quem="assistente" texto={turno.resposta_do_assistente} verde />
        </div>
      )}
    </div>
  )
}

function Fala({
  quem,
  texto,
  verde,
}: {
  quem: string
  texto: string | null
  verde?: boolean
}) {
  return (
    <div className="flex gap-3">
      <div className="w-20 flex-shrink-0 pt-1 text-xs text-gray-500">{quem}</div>
      <div
        className={cn(
          'flex-grow whitespace-pre-wrap rounded-lg px-3 py-2 text-sm text-gray-200',
          verde ? 'bg-emerald-500/5' : 'bg-white/5'
        )}
      >
        {texto ?? '—'}
      </div>
    </div>
  )
}

/**
 * Uma chamada de ferramenta — o que entrou e o que saiu.
 *
 * **NÃO CONHECE FERRAMENTA NENHUMA, e é isso que a faz servir às futuras.**
 * Mostrar `doc_type`, `consulta` ou nome de arquivo por código significaria o
 * painel aprender o vocabulário do setor (regra de ouro 1) e ganhar um `if`
 * por ferramenta nova. Aqui `arguments` e `result` são renderizados como
 * dados: ferramenta que nasce amanhã aparece completa sem uma linha a mais.
 *
 * **E É POR ISSO QUE O PAYLOAD EXPIRA COM A CONVERSA.** O que ele carrega é
 * dado do cliente — a consulta que ele digitou, o nome do documento, o
 * identificador dele. Desde 20/09 `tool_calls` entra no expurgo junto das
 * mensagens; exibi-lo sem isso recriaria a inconsistência que o ADR-0071
 * corrigiu.
 */
function ChamadaDeFerramenta({ chamada }: { chamada: unknown }) {
  const dados = (chamada ?? {}) as Record<string, unknown>
  const nome = typeof dados.name === 'string' ? dados.name : 'ferramenta'
  const ok = dados.success !== false && !dados.error
  const ms = typeof dados.execution_time_ms === 'number' ? dados.execution_time_ms : null

  return (
    <div className="rounded-lg border border-indigo-500/20 bg-indigo-500/5">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <span className="font-mono text-xs text-indigo-300">{nome}</span>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[11px]',
            ok ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
          )}
        >
          {ok ? 'ok' : 'falhou'}
        </span>
        <span className="flex-grow" />
        {ms !== null && (
          <span className="text-[11px] tabular-nums text-gray-500">
            {ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`}
          </span>
        )}
      </div>
      {typeof dados.error === 'string' && dados.error && (
        <p className="border-t border-red-500/20 px-3 py-2 text-xs text-red-300">
          {dados.error}
        </p>
      )}
      <Payload rotulo="pediu" valor={dados.arguments} />
      <Payload rotulo="devolveu" valor={dados.result} />
    </div>
  )
}

/** Um lado do payload, chave a chave, sem interpretar o conteúdo. */
function Payload({ rotulo, valor }: { rotulo: string; valor: unknown }) {
  if (valor === null || valor === undefined) return null
  const objeto =
    typeof valor === 'object' && !Array.isArray(valor)
      ? (valor as Record<string, unknown>)
      : null
  if (objeto && Object.keys(objeto).length === 0) return null

  return (
    <div className="border-t border-indigo-500/15 px-3 py-2">
      <div className="mb-1 text-[10px] uppercase tracking-wider text-gray-600">
        {rotulo}
      </div>
      {objeto ? (
        <dl className="grid grid-cols-[minmax(0,10rem)_1fr] gap-x-3 gap-y-1">
          {Object.entries(objeto).map(([chave, v]) => (
            <Fragment key={chave}>
              <dt className="truncate font-mono text-[11px] text-gray-500">
                {chave}
              </dt>
              <dd className="break-words font-mono text-[11px] text-gray-300">
                {escrever(v)}
              </dd>
            </Fragment>
          ))}
        </dl>
      ) : (
        <p className="break-words font-mono text-[11px] text-gray-300">
          {escrever(valor)}
        </p>
      )}
    </div>
  )
}

/**
 * Um valor legível — e a lista longa DIZ que foi cortada.
 *
 * Cortar em silêncio faria "achou 12 documentos" parecer "achou 3", que é o
 * tipo de número errado que manda alguém procurar defeito onde não há.
 */
function escrever(valor: unknown): string {
  if (valor === null || valor === undefined) return '—'
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não'
  if (typeof valor === 'string' || typeof valor === 'number') return String(valor)
  if (Array.isArray(valor)) {
    if (valor.length === 0) return 'nenhum'
    const mostrados = valor.slice(0, 5).map((v) => escrever(v))
    return valor.length > 5
      ? `${mostrados.join(', ')} … (+${valor.length - 5} de ${valor.length})`
      : mostrados.join(', ')
  }
  return JSON.stringify(valor)
}
